(() => {
  if (!window.customElements || customElements.get('tm-logo-rail')) return;
  class LogoRail extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.dataset.enabled !== 'true') return;
      this.track = this.querySelector('[data-logo-track]');
      this.group = this.querySelector('[data-logo-group]');
      if (!this.track || !this.group || this.group.children.length < 2) return;
      this.controller = new AbortController();
      const on = (el, type, fn, opts = {}) => el.addEventListener(type, fn, { ...opts, signal: this.controller.signal });
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Number(this.dataset.speed) || 25;
      this.hover = this.focused = false;
      this.visible = true;
      this.width = this.last = this.remainder = this.resumeAt = 0;
      this.drag = null;
      this.classList.add('is-ready');

      this.measure = () => {
        const width = this.group.getBoundingClientRect().width;
        if (!width) return;
        const phase = this.width ? (this.track.scrollLeft % this.width) / this.width : 0;
        const needed = Math.max(2, Math.ceil(this.track.clientWidth * 2 / width));
        let copies = [...this.track.querySelectorAll('[data-clone]')];
        while (copies.length < needed) {
          const copy = this.group.cloneNode(true);
          copy.dataset.clone = '';
          copy.removeAttribute('data-logo-group');
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
          copy.querySelectorAll('a').forEach(a => a.tabIndex = -1);
          this.track.append(copy);
          copies.push(copy);
        }
        while (copies.length > needed) copies.pop().remove();
        if (this.width !== width) this.track.scrollLeft = width * (1 + phase);
        this.width = width;
      };
      this.measure();
      on(this.track, 'pointerenter', e => { if (e.pointerType === 'mouse') this.hover = true; });
      on(this.track, 'pointerleave', () => { this.hover = false; });
      on(this.track, 'focusin', () => { this.focused = true; });
      on(this.track, 'focusout', e => { this.focused = this.track.contains(e.relatedTarget); });
      on(this.track, 'scroll', () => {
        if (!this.width || this.focused) return;
        if (this.track.scrollLeft < this.width * .25) this.track.scrollLeft += this.width;
        else if (this.track.scrollLeft > this.width * 1.75) this.track.scrollLeft -= this.width;
      }, { passive: true });
      on(this.track, 'pointerdown', e => {
        if (e.button !== 0) return;
        this.drag = { id: e.pointerId, x: e.clientX, last: e.clientX, y: e.clientY, moved: false, mouse: e.pointerType === 'mouse' };
        this.resumeAt = Infinity;
      });
      on(this.track, 'pointermove', e => {
        const g = this.drag;
        if (!g || g.id !== e.pointerId) return;
        if (!g.moved && Math.abs(e.clientX - g.x) > 7 && Math.abs(e.clientX - g.x) > Math.abs(e.clientY - g.y)) {
          g.moved = true;
          if (g.mouse) this.track.setPointerCapture?.(g.id);
        }
        if (g.moved && g.mouse) {
          e.preventDefault();
          this.track.scrollLeft -= e.clientX - g.last;
          this.track.classList.add('is-dragging');
        }
        g.last = e.clientX;
      });
      const stop = e => {
        if (!this.drag || this.drag.id !== e.pointerId) return;
        if (this.drag.moved || e.type === 'pointercancel') this.suppress = Date.now() + 400;
        this.drag = null;
        this.track.classList.remove('is-dragging');
        this.resumeAt = performance.now() + 1800;
      };
      on(window, 'pointerup', stop);
      on(window, 'pointercancel', stop);
      on(this.track, 'lostpointercapture', stop);
      on(this.track, 'click', e => {
        if (e.detail !== 0 && Date.now() < (this.suppress || 0)) { e.preventDefault(); e.stopPropagation(); }
      }, { capture: true });
      on(this.track, 'dragstart', e => e.preventDefault());
      on(this.track, 'wheel', () => { this.resumeAt = performance.now() + 2200; }, { passive: true });
      on(document, 'visibilitychange', () => { this.last = 0; });
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(this.measure);
        this.resize.observe(this.group);
        this.resize.observe(this.track);
      }
      if (window.IntersectionObserver) {
        this.intersection = new IntersectionObserver(es => { this.visible = es[0].isIntersecting; });
        this.intersection.observe(this);
      }
      const tick = t => {
        const dt = this.last ? Math.min(48, t - this.last) : 0;
        this.last = t;
        if (!this.reduce.matches && !this.hover && !this.focused && !this.drag && this.visible && !document.hidden && t > this.resumeAt && this.width) {
          this.remainder += dt * this.speed / 1000;
          const step = Math.floor(this.remainder);
          this.remainder -= step;
          this.track.scrollLeft += step;
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      cancelAnimationFrame(this.frame);
      this.resize?.disconnect(); this.intersection?.disconnect();
      this.querySelectorAll('[data-clone]').forEach(el => el.remove());
      this.classList.remove('is-ready');
    }
  }
  customElements.define('tm-logo-rail', LogoRail);
})();
